// React
import { gql, useLazyQuery, useQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import { useEffect, useState, useContext, useCallback } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Tooltip,
} from "@mui/material";

// Custom UI components
import borders from "assets/theme/base/borders";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import SinDatos from "components/Carteles/SinDatos";
import SinResultados from "components/Carteles/SinResultados";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import TableItem from "../../../materiaPrima/components/ModalIngresar/TableItem";
import { MessageManager } from "context";
import ModalProducto from "../../../materiaPrima/components/ModalProducto";
import { formatDate } from "utils/formatters";
import ModalSelectProvision from "../../../materiaPrima/components/ModalIngresar/ModalSelectProvision";
import ModalCambioProducto from "../../../materiaPrima/components/ModalIngresar/ModalCambioProducto";
import ModalRemitos from "../../../materiaPrima/components/ModalIngresar/ModalRemitos";
import ModalHistorialEntregas from "../ModalHistorialEntregas";

const PROVISION_CLOSURE_STATES = [
  "Cerrado con entrega parcial",
  "Completada con entrega parcial",
  "Completada sin entregas",
];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflow: "auto",
  p: { xs: 1, sm: 3 },
};

const columnsBase = [
  { name: "id", desc: "ID", width: "1%", align: "center" },
  { name: "nombre", desc: "Nombre", align: "left" },
  { name: "categoria", desc: "Rubro", align: "left" },
  { name: "entregado", desc: "Entregado", align: "center" },
  { name: "cantidad", desc: "cantidad", align: "center", width: "220px" },
  { name: "porcentaje", desc: "% acumulado", align: "center" },
  { name: "unidad", desc: "Unidad", align: "center", width: "100px" },
  { name: "eliminar", desc: " ", align: "left", width: "1%", noOrder: true },
];

const columnsSelect = columnsBase.filter(({ name }) => name !== "eliminar");

export default function ModalSolicitarEntregaProveedor({
  open,
  handleClose,
  selectedRemito,
  idSucursal = 1,
  refetchProductos,
}) {
  // Context
  const { handleSnackbar } = useContext(MessageManager);
  const [filterQuantity, setFilterQuantity] = useState(false);

  // States
  const [openMenu, setOpenMenu] = useState(false);
  const [productoToAdd, setProductoToAdd] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [productosRemito, setProductosRemito] = useState([]);
  const [openModalProducto, setOpenModalProducto] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const [openAddMenu, setOpenAddMenu] = useState(false);
  const [remito, setRemito] = useState("");
  const [openSelectProvision, setOpenSelectProvision] = useState(false);
  const [selectedProvision, setSelectedProvision] = useState();
  const [cambioProductoData, setCambioProductoData] = useState(null);
  const [openRemitosModal, setOpenRemitosModal] = useState(false);
  const [openHistorialEntregas, setOpenHistorialEntregas] = useState(false);
  const [camposConError, setCamposConError] = useState(new Set());
  const [mostrarTooltips, setMostrarTooltips] = useState(false);

  // Other
  const { light } = colors;
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;

  const [getProductos, { data: dataMP, loading }] = useLazyQuery(
    gql`
      query getProductos($page: Int, $order: Order, $filter: JSON) {
        paginationInfo {
          pages
          total
        }
        materiasPrimas(pagination: { page: $page, limit: 50 }, order: $order, filter: $filter) {
          id
          codigo
          tipo
          categoria
          nombre
          disabled
          precio
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
    }
  );
  const [solicitarEntrega, { loading: loadingSave }] = useMutation(gql`
    mutation solicitarEntregaProveedor($input: SolicitudEntregaProveedorInput!) {
      solicitarEntregaProveedor(input: $input) {
        id
        emailEnviado
        errorEmail
      }
    }
  `, {
    onCompleted: (data) => {
      if (data.solicitarEntregaProveedor.emailEnviado) {
        handleSnackbar("Solicitud de entrega enviada correctamente por email", "success");
      } else if (data.solicitarEntregaProveedor.errorEmail) {
        handleSnackbar(`Solicitud creada pero error en email: ${data.solicitarEntregaProveedor.errorEmail}`, "warning");
      } else {
        handleSnackbar("Solicitud creada pero no se pudo enviar email (proveedor sin email)", "warning");
      }
      handleClose();
    },
    onError: (error) => {
      handleSnackbar(`Error al crear solicitud: ${error.message}`, "error");
    }
  });
  const [getSucursal, { data: sucursal }] = useLazyQuery(
    gql`
      query getSucursal($id: ID!) {
        sucursal(id: $id) {
          id
          nombre
          sede {
            id
            nombre
          }
        }
      }
    `
  );

  // useEffects
  useEffect(() => {
    setRemito();
    setProductosRemito([]);
    setSelectedProvision();
    if (open && selectedRemito) {
      setSelectedProvision(selectedRemito.provision);

      /* let mpCargados = selectedRemito.movimientoDetalles.map((item) => {
        const provisionDetalle = selectedRemito.provision?.detalles?.find(det => {
          return det.id === item.idProvisionDetalle;
        });
        return {
          ...item.producto,
          idProvisionDetalle: null,
          cantidad: item.cantidad,

          cantidad: item.cantidad,
          cantidadEntregada: provisionDetalle?.cantidadEntregada ?? 0,
          cantidadRequerida: provisionDetalle?.cantidad ?? 0,
          enabled: true
        }
      }); */

      let mpCargados = selectedRemito.provision?.detalles.map((item) => {
        const movimientoDetalle = selectedRemito.movimientoDetalles.find((det) => {
          return det.idProvisionDetalle === item.id;
        });
        return {
          ...(movimientoDetalle?.materiaPrima ?? item.materiaPrimaRecepcion ?? item.materiaPrima),
          mpOriginal: item.materiaPrima,
          idProvisionDetalle: null,
          cantidad: movimientoDetalle?.cantidad,
          cantidadEntregada: item?.cantidadEntregada ?? 0,
          cantidadRequerida: item?.cantidad ?? 0,
          enabled: true,
        };
      });
      setProductosRemito(mpCargados);
    } else {
      setRemito({ horaEntregaInicio: '08:00', horaEntregaFin: '11:00' });
      setProductosRemito([]);
      setSelectedProvision();
    }
  }, [open]);

  useEffect(() => {
    if (productoToAdd && dataMP?.materiasPrimas?.length > 0) {
      if (dataMP.materiasPrimas.find((producto) => producto.id === productoToAdd)) {
        handleAddProduct(dataMP.materiasPrimas.find((mp) => mp.id === productoToAdd));
      }
      setProductoToAdd();
    }
  }, [dataMP]);

  useEffect(() => {
    if (idSucursal && open) getSucursal({ variables: { id: idSucursal } });
  }, [idSucursal, open]);

  // Handlers
  const handleChanges = (event) => {
    setRemito({ ...remito, [event.target.name]: event.target.value });
  };

  const handleOpenAddMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenAddMenu(true);
  };

  const handleCloseAddMenu = () => {
    setAnchorEl(null);
    setOpenAddMenu(false);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const handleUpdateItem = (item) => {
    setProductosRemito((prev) =>
      prev.map((prevItem) => {
        if (prevItem.id === item.id) {
          return item;
        }
        return prevItem;
      })
    );
  };

  const handleAddProduct = (product) => {
    setProductosRemito([...productosRemito, product]);
  };

  const crearTooltipError = useCallback((campo, children, mensajeError) => {
    if (!open) return children;

    const tieneError = camposConError.has(campo) && mostrarTooltips;

    if (tieneError) {
      return (
        <Tooltip
          title={mensajeError || "Este campo es obligatorio"}
          placement="top"
          open={true}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'error.main',
                '& .MuiTooltip-arrow': {
                  color: 'error.main',
                },
              },
            },
          }}
        >
          {children}
        </Tooltip>
      );
    }

    return children;
  }, [open, camposConError, mostrarTooltips]);

  const renderColumnsBase = columnsBase.map(({ name, desc, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columnsBase.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <SoftBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={0.7}
        borderBottom={`${borderWidth[1]} solid ${light.main}`}
      >
        {desc ? desc.toUpperCase() : name.toUpperCase()}
      </SoftBox>
    );
  });

  const renderColumnsSelect = columnsSelect.map(({ name, desc, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columnsSelect.length - 1) {
      pl = 1;
      pr = 1;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <SoftBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={0.7}
        borderBottom={`${borderWidth[1]} solid ${light.main}`}
      >
        {desc ? desc.toUpperCase() : name.toUpperCase()}
      </SoftBox>
    );
  });

  const handleSave = () => {
    const errores = new Set();
    const items = productosRemito
      .filter((item) => item.enabled)
      .map((item) => ({
        idMateriaPrima: item.id,
        idProvisionDetalle: item.idProvisionDetalle,
        nombre: item?.nombre,
        cantidad: item.cantidad,
        unidad: item?.tipoUnidad?.abreviatura || item?.tipoUnidad?.nombre || "",
      }));

    const input = {
      idProveedor: selectedProvision?.proveedor?.id,
      idProvision: remito?.idProvision,
      fechaEntrega: remito?.fechaEntrega,
      horaEntregaInicio: remito?.horaEntregaInicio,
      horaEntregaFin: remito?.horaEntregaFin,
      observaciones: remito?.observaciones,
      items,
    };

    if (!input.fechaEntrega) errores.add("fechaEntrega");
    if (!input.idProveedor) errores.add("idProveedor");
    if (items.length === 0 || (items.length > 0 && items.every((item) => item.cantidad <= 0))) errores.add("items");

    if (errores.size > 0) {
      setCamposConError(errores);
      setMostrarTooltips(true);

      // Ocultar tooltips después de 5 segundos
      setTimeout(() => {
        setMostrarTooltips(false);
        setCamposConError(new Set());
      }, 5000);

      // Mostrar mensaje de error
      handleSnackbar("Por favor verifique que los campos estén completos y sean válidos", "error");
      return;
    }

    solicitarEntrega({ variables: { input } })
      .then(() => {
        handleSnackbar("La solicitud se envió al proveedor", "success");
        handleClose();
        refetchProductos && refetchProductos();
      })
      .catch(() => {
        handleSnackbar("Ocurrió un error al enviar la solicitud", "error");
      });
  };

  const handleSelection = (selected) => {
    setSelectedProvision(selected);
    const provisionCerrada = PROVISION_CLOSURE_STATES.includes(selected?.estado);

    // ProductosRemito -> prducto + cantidad
    let mpCargados = selected.detalles.map((item) => {
      const multiplicador = item?.materiaPrima?.tipoUnidad?.multiplicador || 1;
      // const cantidadEntregadaAjustada = item.cantidadEntregada === 0 ? 0 : (item.cantidadEntregada / multiplicador);

      return {
        ...(item.materiaPrimaRecepcion ?? item.materiaPrima),
        mpOriginal: item.materiaPrima,
        idProvisionDetalle: item.id,
        cantidad: item.cantidad - item.cantidadEntregada,
        cantidadEntregada: item.cantidadEntregada,
        cantidadRequerida: item.cantidad,
        enabled: !provisionCerrada && item.cantidad - item.cantidadEntregada > 0,
        tipoUnidad: item.materiaPrima?.tipoUnidad,
      };
    });

    setRemito((prev) => ({
      ...prev,
      idProvision: selected.id,
      idPedidoCompra: selected.pedidoCompra?.id,
    })); console.log("IDPEDIDO:", selected.pedidoCompra?.id);
    setProductosRemito(mpCargados);
  };

  const handleCambioProducto = ({ selected, data }) => {
    setProductosRemito((prev) => {
      let newProductos = [...prev];
      newProductos[data.index] = {
        ...newProductos[data.index],
        ...selected,
      };
      return newProductos;
    });
  };

  const provisionCerrada = PROVISION_CLOSURE_STATES.includes(selectedProvision?.estado);
  const pedidoId = selectedRemito?.provision?.pedidoCompra?.id || selectedProvision?.pedidoCompra?.id;
  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
      >
        <Fade in={open}>
          <Card sx={style}>
            <SoftBox display="flex" flexDirection="column" gap={2}>
              <Card>
                <SoftBox display="flex" flexDirection="column" gap={2} p={{ xs: 2, sm: 3 }}>
                  <SoftBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={2}
                  >
                    <SoftTypography variant="h6">Solicitud de entrega</SoftTypography>
                    <SoftBox display="flex" justifyContent="space-between" alignItems="flex-end" gap={1}>
                      {crearTooltipError("idProveedor",
                        <SoftButton
                          color={selectedProvision ? "uproYellow" : "primary"}
                          circular
                          onClick={() => setOpenSelectProvision(true)}
                        >
                          {selectedProvision ? (
                            <>
                              Cambiar orden&nbsp;<Icon>autorenew</Icon>
                            </>
                          ) : (
                            <>
                              Seleccionar orden&nbsp;<Icon>arrow_outward</Icon>
                            </>
                          )}
                        </SoftButton>)}
                      {selectedProvision?.proveedor?.id && (
                        <Tooltip title="Ver historial de envíos de solicitud" placement="top">
                          <SoftButton
                            color="info"
                            iconOnly
                            circular
                            onClick={() => setOpenHistorialEntregas(true)}
                          >
                            <Icon sx={{ fontWeight: "light" }}>history</Icon>
                          </SoftButton>
                        </Tooltip>
                      )}
                    </SoftBox>
                  </SoftBox>
                  <Grid container spacing={2}>

                    <Grid item xs={12} lg={8}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <InputLabel htmlFor="sede">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Sucursal de destino
                            </SoftTypography>
                          </InputLabel>
                          {selectedRemito ? (
                            <SoftTypography variant="h6" fontWeight="bold">
                              {`${sucursal?.sucursal?.sede?.nombre} - ${sucursal?.sucursal?.nombre}`}
                            </SoftTypography>
                          ) : sucursal?.sucursal?.nombre ? (
                            <SoftInput
                              disabled
                              value={`${sucursal?.sucursal?.sede?.nombre} - ${sucursal?.sucursal?.nombre}`}
                            />
                          ) : (
                            <SoftTypography variant="h6" fontWeight="bold">
                              <Skeleton width={100} />
                            </SoftTypography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <InputLabel htmlFor="fechaEntrega">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Fecha de Entrega
                              {!selectedRemito && (
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                  &nbsp;*
                                </SoftTypography>
                              )}
                            </SoftTypography>
                          </InputLabel>
                          {selectedRemito ? (
                            <SoftTypography variant="h6" fontWeight="bold">
                              {selectedRemito?.fechaEntrega}
                            </SoftTypography>
                          ) : (
                            crearTooltipError("fechaEntrega",
                              <SoftInput
                                label="fechaEntrega"
                                type="date"
                                name="fechaEntrega"
                                value={remito?.fechaEntrega || ""}
                                onChange={handleChanges}
                              />)
                          )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <InputLabel htmlFor="horaEntregaInicio">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Hora de inicio
                            </SoftTypography>
                          </InputLabel>
                          {selectedRemito ? (
                            <SoftTypography variant="h6" fontWeight="bold">
                              {selectedRemito?.horaEntregaInicio}
                            </SoftTypography>
                          ) : (
                            <SoftInput
                              label="horaEntregaInicio"
                              type="time"
                              name="horaEntregaInicio"
                              value={remito?.horaEntregaInicio || "08:00"}
                              onChange={handleChanges}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <InputLabel htmlFor="horaEntregaFin">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Hora de fin
                            </SoftTypography>
                          </InputLabel>
                          {selectedRemito ? (
                            <SoftTypography variant="h6" fontWeight="bold">
                              {selectedRemito?.horaEntregaFin}
                            </SoftTypography>
                          ) : (
                            <SoftInput
                              label="horaEntregaFin"
                              type="time"
                              name="horaEntregaFin"
                              value={remito?.horaEntregaFin || "11:00"}
                              onChange={handleChanges}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <InputLabel htmlFor="observaciones">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Observaciones
                            </SoftTypography>
                          </InputLabel>
                          {selectedRemito ? (
                            <SoftTypography variant="h6" fontWeight="bold">
                              {selectedRemito?.observaciones}
                            </SoftTypography>
                          ) : (
                            <SoftInput
                              label="observaciones"
                              placeholder="Inserte un comentario"
                              type="text"
                              name="observaciones"
                              value={remito?.observaciones || ""}
                              onChange={handleChanges}
                              multiline
                              rows={5}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </SoftBox>
              </Card>

              <Card>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={3}
                  pb={0}
                >
                  <SoftBox>
                    <SoftTypography variant="h6">Productos</SoftTypography>
                  </SoftBox>
                  {selectedRemito && (
                    <SoftBox display="flex" justifyContent="end" alignItems="center" gap={1}>
                      <SoftTypography variant="caption">
                        Mostrar solo de este movimiento
                      </SoftTypography>
                      <Switch
                        checked={filterQuantity}
                        onChange={() => setFilterQuantity(!filterQuantity)}
                      />
                    </SoftBox>
                  )}
                </SoftBox>
                <SoftBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                  }}
                >
                  {(productosRemito || []).length > 0 ? (
                    <TableContainer>
                      <Table
                        aria-label="Productos de la solicitud de entrega"
                        sx={{
                          '& td[data-label="Cantidad a ingresar"] > div': {
                            flexWrap: "wrap",
                            gap: 1,
                            "& .MuiInputBase-root": {
                              flex: "0 0 120px",
                              width: "120px",
                              minWidth: "120px",
                            },
                            "& > .MuiTypography-root": { ml: 0 },
                          },
                          "@media (max-width:1199px)": {
                            display: "block",
                            "& thead": { display: "none" },
                            "& tbody": { display: "block" },
                            "& tr": {
                              display: "grid",
                              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                              border: "1px solid",
                              borderColor: "light.main",
                              borderRadius: 2,
                              m: 1,
                            },
                            "& td": { display: "block", minWidth: 0, overflowWrap: "anywhere" },
                            '& td[data-label="Nombre de compra"], & td[data-label="Cantidad a ingresar"]': {
                              gridColumn: "1 / -1",
                            },
                            "& td::before": {
                              content: "attr(data-label)",
                              display: "block",
                              fontSize: "10px",
                              fontWeight: "bold",
                              color: "secondary.main",
                              textAlign: "left",
                            },
                          },
                        }}
                      >
                        <SoftBox component="thead">
                          {selectedRemito || provisionCerrada ? (
                            <TableRow>{renderColumnsSelect}</TableRow>
                          ) : (
                            <TableRow>{renderColumnsBase}</TableRow>
                          )}
                        </SoftBox>
                        {crearTooltipError("items",
                          <TableBody>
                            {(productosRemito || [])
                              .filter((i) => !filterQuantity || i.cantidad > 0)
                              .map((item, index) => {
                                return (
                                  <TableItem
                                    key={item.id}
                                    item={item}
                                    handleUpdate={handleUpdateItem}
                                    disabled={Boolean(selectedRemito) || provisionCerrada}
                                    cambiarProducto={(data) => {
                                      setCambioProductoData({ index, ...data });
                                    }}
                                  />
                                );
                              })}
                          </TableBody>, "Debe haber al menos un producto con cantidad mayor a 0")}
                      </Table>
                    </TableContainer>
                  ) : (
                    <SinDatos />
                  )}
                </SoftBox>
              </Card>

              {!selectedRemito && (
                <Card>
                  <SoftBox
                    display="flex"
                    justifyContent="end"
                    alignItems="center"
                    p={2}
                    flexDirection={{
                      xs: "column",
                      sm: "row",
                    }}
                    gap={2}
                  >
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                    >
                      <SoftButton color="primary" circular onClick={handleClose} fullWidth>
                        <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                        &nbsp;Cancelar
                      </SoftButton>
                    </SoftBox>
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                    >
                      <SoftButton
                        color="uproGreen"
                        circular
                        fullWidth
                        disabled={loadingSave || provisionCerrada}
                        onClick={handleSave}
                      >
                        {loadingSave ? (
                          <CircularProgress size={15} color="white" />
                        ) : (
                          <Icon sx={{ fontWeight: "light" }}>email</Icon>
                        )}
                        &nbsp;Solicitar
                      </SoftButton>
                    </SoftBox>
                  </SoftBox>
                </Card>
              )}
            </SoftBox>
            <ModalProducto
              id={selectedItem?.id || null}
              tipo={selectedItem?.tipo || null}
              open={openModalProducto}
              handleClose={() => {
                setOpenModalProducto(false);
                handleCloseMenu();
                handleCloseAddMenu();
              }}
              refetch={() => { }}
              onCreate={(newId) => {
                getProductos({
                  variables: {
                    page: 1,
                    filter: {
                      disabled: false,
                      id: newId,
                    },
                  },
                });
                setProductoToAdd(newId);
              }}
            />

            <ModalCambioProducto
              data={cambioProductoData}
              handleClose={() => setCambioProductoData(null)}
              setSelection={handleCambioProducto}
            />

            <ModalSelectProvision
              open={openSelectProvision}
              handleClose={() => setOpenSelectProvision(false)}
              setSelection={handleSelection}
              solicitarPliego={true}
            />
          </Card>
        </Fade>
      </Modal>

      <ModalRemitos
        open={openRemitosModal}
        handleClose={() => setOpenRemitosModal(false)}
        idProvision={selectedProvision?.id}
        provisionData={selectedProvision}
      />

      <ModalHistorialEntregas
        open={openHistorialEntregas}
        handleClose={() => setOpenHistorialEntregas(false)}
        idProveedorFijo={selectedProvision?.proveedor?.id}
      />
    </>
  );
};

ModalSolicitarEntregaProveedor.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  selectedRemito: PropTypes.object,
  idSucursal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refetchProductos: PropTypes.func,
};


